<!--
// app/components/Shared/SeasonsFilter.vue
-->
<script setup lang="ts">
import type { Season } from "~/src/season-cluster/domain/models/01.Season";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useSeasons } from "~/app/composables/season/seasons";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";

type Props = {
  combinedWithMoreFilters?: boolean;
  seasonId: number | undefined;
  seasonClusterId: number | undefined;
};

const props = withDefaults(defineProps<Props>(), {
  combinedWithMoreFilters: false,
  seasonId: undefined,
  seasonClusterId: undefined,
});

const emit = defineEmits(["change"]);

const seasonId = defineModel<number>("seasonId");
const seasonClusterId = defineModel<number>("seasonClusterId");

const { seasonClusters } = useCompetitionStore();
const { findCurrentSeasonClusterSeasons } = useSeasons();
const { changeSeason } = useSeasonFilter();

const seasonOptions = computed((): Season[] => {
  if (!seasonClusterId.value) return [] as Season[];

  const selectedSeasonCluster = seasonClusters.find(sc => sc.id === seasonClusterId.value);
  return selectedSeasonCluster ? findCurrentSeasonClusterSeasons(selectedSeasonCluster) : ([] as Season[]);
});

const handleSeasonChange = (seasonIdArg: number) => {
  seasonId.value = seasonIdArg;
  changeSeason(seasonClusterId.value, seasonIdArg);
  emit("change", seasonId.value);
};

watch(seasonClusterId, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    handleSeasonChange(seasonOptions.value[seasonOptions.value.length - 1].id);
  }
});
</script>

<template>
  <div
    :class="[
      'flex lg:px-0 rounded-md shadow-sm justify-end my-6',
      combinedWithMoreFilters ? 'max-w-full sm:max-w-max' : '',
    ]"
    v-if="seasonOptions.length"
  >
    <button
      v-for="(season, index) in seasonOptions"
      :key="index"
      @click="handleSeasonChange(season.id)"
      :class="[
        'kql-season-button',
        {
          '!bg-primary !text-black': season.id === seasonId,
        },
      ]"
    >
      {{ season.displayName }}
    </button>

    <select
      id="seasonCluster"
      class="kql-select text-[10px] bigsm:text-xs lg:text-sm border-l rounded-e-lg"
      v-model="seasonClusterId"
    >
      <option v-for="seasonCluster in seasonClusters" :key="seasonCluster.id" :value="seasonCluster.id">
        {{ seasonCluster.name }}
      </option>
    </select>
  </div>
  <div v-else class="flex px-4 lg:px-0 shadow-sm justify-end my-6">
    <div class="h-10 w-full max-w-xl bg-gray-800 rounded-lg animate-pulse"></div>
  </div>
</template>

<style scoped></style>
